import { AutoCenter, Mask, MaskProps, SpinLoading } from 'antd-mobile';
import { mergeProps } from 'antd-mobile/es/utils//with-default-props';
import { PropagationEvent } from 'antd-mobile/es/utils//with-stop-propagation';
import { GetContainer } from 'antd-mobile/es/utils/render-to-container';
import { CheckOutline, CloseOutline } from 'antd-mobile-icons';
import classNames from 'classnames';
import React, { ReactNode, useMemo } from 'react';

const classPrefix = 'custom-toast';

export interface ToastProps {
  afterClose?: () => void;
  maskStyle?: MaskProps['style'];
  maskClassName?: string;
  maskClickable?: boolean;
  content?: ReactNode;
  icon?: 'success' | 'fail' | 'loading' | React.ReactNode;
  duration?: number;
  position?: 'top' | 'bottom' | 'center';
  visible?: boolean;
  getContainer?: GetContainer;
  stopPropagation?: PropagationEvent[];
  // 动画参数
  animationStartOffset?: number; // 动画开始位移（px）
  animationEndOffset?: number; // 动画结束位移（px）
  animationType?: 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right' | 'fade'; // 动画类型
}

const defaultProps = {
  maskClickable: true,
  stopPropagation: ['click'],
};

export const InternalToast: React.FC<ToastProps> = (p) => {
  const props = mergeProps(defaultProps, p);
  const {
    maskClickable,
    content,
    icon,
    position,
    animationStartOffset = 30,
    animationEndOffset = 0,
    animationType,
  } = props;

  const iconElement = useMemo(() => {
    if (icon === null || icon === undefined) return null;
    switch (icon) {
      case 'success':
        return <CheckOutline className={`${classPrefix}-icon-success`} />;
      case 'fail':
        return <CloseOutline className={`${classPrefix}-icon-fail`} />;
      case 'loading':
        return <SpinLoading color="white" className={`${classPrefix}-loading`} />;
      default:
        return icon;
    }
  }, [icon]);

  const top = useMemo(() => {
    switch (position) {
      case 'top':
        return '20%';
      case 'bottom':
        return '80%';
      default:
        return '50%';
    }
  }, [position]);

  // 生成动态动画CSS变量和初始位置（只有当animationType存在时才生成）
  const animationStyle = useMemo(() => {
    if (!animationType) return {};

    // 根据动画类型设置初始和最终位置
    let initialTransform = '';
    switch (animationType) {
      case 'slide-up':
      case 'slide-down':
        initialTransform = `translateY(-50%) translateY(${animationStartOffset}px)`;
        break;
      case 'slide-left':
      case 'slide-right':
        initialTransform = `translateY(-50%) translateX(${animationStartOffset}px)`;
        break;
      case 'fade':
        initialTransform = 'translateY(-50%)';
        break;
      default:
        initialTransform = 'translateY(-50%)';
    }

    return {
      '--animation-start-offset': `${animationStartOffset}px`,
      '--animation-end-offset': `${animationEndOffset}px`,
      // 设置初始位置
      transform: initialTransform,
      opacity: animationType === 'fade' ? 0 : 0.8,
    } as React.CSSProperties;
  }, [animationStartOffset, animationEndOffset, animationType]);

  return (
    <Mask
      visible={props.visible}
      destroyOnClose
      opacity={0}
      disableBodyScroll={!maskClickable}
      getContainer={props.getContainer}
      afterClose={props.afterClose}
      style={{
        pointerEvents: maskClickable ? 'none' : 'auto',
        ...props.maskStyle,
      }}
      className={classNames(`${classPrefix}-mask`, props.maskClassName)}
      stopPropagation={props.stopPropagation}
    >
      <div className={classNames(`${classPrefix}-wrap`)}>
        <div
          style={{ top, ...animationStyle }}
          className={classNames(
            `${classPrefix}-main`,
            icon ? `${classPrefix}-main-icon` : `${classPrefix}-main-text`,
            animationType ? `${classPrefix}-${animationType}` : undefined,
          )}
        >
          {iconElement && <div className={`${classPrefix}-icon`}>{iconElement}</div>}
          <AutoCenter>{content}</AutoCenter>
        </div>
      </div>
    </Mask>
  );
};
